import { AppBar, Button, Stack, Toolbar, Box, Paper, Typography, Grid, IconButton} from "@mui/material"
import { useTranslation } from 'react-i18next';
import { grey } from '@mui/material/colors'
import { getDynamicSettingInfo, getBuyersRentersInformation } from './utils';
import { memo, useEffect, useMemo, useState } from "react";
import QuotationInfoCardsWrapper from "../QuotationInfo/QuotationInfoCardsWrapper";
import RowDetails from "../../components/RowDetails";
import { Edit } from "@mui/icons-material";
import EditBuyerRenterInformationDialog from "./EditBuyerRenterInformationDialog";
import ConfirmBox from "../../components/ConfirmBox";
import { useDispatch, useSelector } from "react-redux";
import { toastActions } from "../../_actions";
import { createAndSendQuotationForDigitalSigning, getBuyerInfo, getQuotationsDefaults } from "../../apis/buyersGuideApis";
import Loader from '../../components/Loader';
import { history, ModeOfSigningKeysForQuotation } from "../../_helpers";


const GenerateQuotation = ({ onClose }) => {
    const { t } = useTranslation()
    const [selectedMode, setSelectedMode] = useState('mobile');
    const dispatch = useDispatch()
    const { selected } = useSelector((state) => state.buildings);
    const app = useSelector((state) => state.app);
    const [openEditRenterInfoDialog, setOpenEditRenterInfoDialog] = useState(false);
    const [buyersRentersRaw, setBuyersRentersRaw] = useState();
    const buyersRenters = getBuyersRentersInformation(t, buyersRentersRaw);
    const [isOpenGenerateConfirmModal, setIsOpenGenerateConfirmModal] = useState(false)
    const [isPerson1Checked, setIsPerson1Checked] = useState(false);
    const [isPerson2Checked, setIsPerson2Checked] = useState(false);
    const [quotationDefaults, setQuotationDefaults] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [generateQuotationLoading,setGenerateQuotationLoading] = useState(false)
    const [buyerInfoLoading,setBuyerInfoLoading] = useState(false)

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];

        if (!file) return;

        const maxSize = 8 * 1024 * 1024; 

        if (file.size > maxSize) {
            dispatch(toastActions.error(t("buyersGuide.quotation.modeOfSign.validation.fileSizeExceeded")));
            e.target.value = null;
            return;
        }

        setUploadedFile(file);
    };

    const handleChange = (value) => {
        setSelectedMode(value);
    };

    const WHO = {
        person1: t("general.person") + " 1",
        person2: t("general.person") + " 2",
        relation: t("general.relation")
    };

    const fetchBuyerInfo = async () => {
        if (!selected?.buildingId) return;

        try {
            setBuyerInfoLoading(true);
            const res = await getBuyerInfo(selected.buildingId, app);
            setBuyersRentersRaw(res.data);
        } catch (err) {
            console.error(err);
            dispatch(toastActions.error(t("error.header")));
        } finally {
            setBuyerInfoLoading(false);
        }
    };
    useEffect(() => {
        fetchBuyerInfo();
    }, [selected?.buildingId]);


    useEffect(() => {
        if (!selected?.buildingId) return;

        setGenerateQuotationLoading(true);

        getQuotationsDefaults(selected.buildingId)
            .then(res => {
                setQuotationDefaults({
                    ...res.data,
                    isPriceIncludingVAT: res.data?.isPriceIncludingVAT ?? false,
                    modeOfSigning: res.data?.modeOfSigning
                        ?? ModeOfSigningKeysForQuotation.DigitalSign
                });
            })
            .catch(err => {
                console.error(err);
                dispatch(toastActions.error(t("error.header")));
            })
            .finally(() => {
                setGenerateQuotationLoading(false);
            });

    }, [dispatch, selected?.buildingId]);

    const hasPerson2 = !!buyersRentersRaw?.p2?.personId;
  
    const isBuyerAsPerson = useMemo(() => {
        return buyersRentersRaw?.type === 0
      }, [buyersRentersRaw?.type])

    const phoneRegex = /^\+[1-9]\d{7,14}$/;

    const validateSigning = () => {

        // Only validate for mobile signing
        if (selectedMode !== "mobile") return true;

        const p1 = buyersRentersRaw?.p1;
        const p2 = buyersRentersRaw?.p2;
        const hasP1 = !!p1?.personId;
        const hasP2 = !!p2?.personId;

        // -------- ORG BUYER --------
        if (!isBuyerAsPerson) {

            const mobile = buyersRentersRaw?.org?.relationMobile?.trim();

            if (!mobile) {
                dispatch(toastActions.error(t("buyersGuide.quotation.modeOfSign.validation.mobileRequired", { who: WHO.relation })))
                return false;
            }

            if (!phoneRegex.test(mobile)) {
                dispatch(toastActions.error(t("buyersGuide.quotation.modeOfSign.validation.mobileInvalid", { who: WHO.relation })))
                return false;
            }

            return true;
        }

        // -------- PERSON BUYER --------

        // CASE: Only Person-1 exists → NO checkboxes in UI
        if (hasP1 && !hasP2) {

            if (!p1?.mobile?.trim()) {
                dispatch(toastActions.error(t("buyersGuide.quotation.modeOfSign.validation.mobileRequired", { who: WHO.person1 })))
                return false;
            }

            if (!phoneRegex.test(p1.mobile)) {
                dispatch(toastActions.error(t("buyersGuide.quotation.modeOfSign.validation.mobileInvalid", { who: WHO.person1 })))
                return false;
            }

            return true;
        }

        // CASE: Both people exist → checkboxes are shown
        if (hasP1 && hasP2) {

            // Must select at least one
            if (!isPerson1Checked && !isPerson2Checked) {
               dispatch(toastActions.error(t("buyersGuide.quotation.modeOfSign.validation.selectAtLeastOnePerson")))
                return false;
            }

            // Validate P1 ONLY IF SELECTED
            if (isPerson1Checked) {

                if (!p1?.mobile?.trim()) {
                    dispatch(toastActions.error(t("buyersGuide.quotation.modeOfSign.validation.mobileRequired", { who: WHO.person1 })))
                    return false;
                }

                if (!phoneRegex.test(p1.mobile)) {
                    dispatch(toastActions.error(t("buyersGuide.quotation.modeOfSign.validation.mobileInvalid", { who: WHO.person1 })))
                    return false;
                }
            }

            // Validate P2 ONLY IF SELECTED
            if (isPerson2Checked) {

                if (!p2?.mobile?.trim()) {
                    dispatch(toastActions.error(t("buyersGuide.quotation.modeOfSign.validation.mobileRequired", { who: WHO.person2 })))
                    return false;
                }

                if (!phoneRegex.test(p2.mobile)) {
                    dispatch(toastActions.error(t("buyersGuide.quotation.modeOfSign.validation.mobileInvalid", { who: WHO.person2 })))
                    return false;
                }
            }
        }

        return true;
    };

    const handleGenerateClick = () => {
        const isValid = validateSigning();
        if (!isValid) return;
        setIsOpenGenerateConfirmModal(true);
    }
    
    const buildQuotationPayload = () => {
        return {
            buildingId: selected?.buildingId,
            quoteId: quotationDefaults?.quoteId ?? null,
            quoteNo: quotationDefaults?.quoteNo ?? 0,
            reportType: quotationDefaults?.reportType,
            closingDate: quotationDefaults?.closingDate,
            signatureType: quotationDefaults?.modeOfSigning,
            isPriceIncludingVAT: quotationDefaults?.isPriceIncludingVAT ?? false,
            isPerson1: hasPerson2 ? (isPerson1Checked ? isPerson1Checked : false) : true,
            isPerson2: hasPerson2 ? (isPerson2Checked ? isPerson2Checked : false) : false,

            drawing: uploadedFile
                ? {
                    name: uploadedFile.name,
                    content: null
                }
                : null
        };
    };

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
        });
    };

    const handleConfirmForGenerateQuotation = async () => {
        try {
            setIsOpenGenerateConfirmModal(false);
            const payload = buildQuotationPayload();

            if (uploadedFile) {
                payload.drawing.content = await fileToBase64(uploadedFile);
            }

            const response = await createAndSendQuotationForDigitalSigning(payload);

            if (response?.success || response?.status === 200) {
                history.push(`/offertes/${quotationDefaults?.quoteId}`);
                dispatch(toastActions.success(t('buyersGuide.quotation.createSuccessfully.toastMessage')));
            } else {
                throw new Error("API returned unsuccessful response");
            }
        } catch (err) {
            console.error(err);
            dispatch(toastActions.error(t("error.header")));

        } finally {
            setGenerateQuotationLoading(false);
        }
    };


    const dynamicSettings = getDynamicSettingInfo(
        t,
        quotationDefaults,
        setQuotationDefaults,
        isPerson1Checked,
        isPerson2Checked,
        setIsPerson1Checked,
        setIsPerson2Checked,
        selectedMode,
        handleChange,
        buyersRentersRaw,
        handleFileUpload,
        uploadedFile
    );

    
    return (
        <>
        {
           ( buyerInfoLoading || generateQuotationLoading)  ?
           <Loader/> : 
        <Stack>
            <AppBar sx={{ position: 'sticky' }}>
                <Toolbar sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button color="inherit" onClick={onClose} >{t('general.cancel')}</Button>
                    <Button color="inherit" onClick={handleGenerateClick} >{t('buyersGuide.options.generate')}</Button>
                </Toolbar>
            </AppBar>
            <Box
                sx={{
                    flexGrow: 1,
                    bgcolor: grey[100],
                    p: 2,
                    overflow: "auto",
                    height: "100%",
                }}>
                <Stack sx={{
                    flexGrow: 1,
                    my: 2,
                }} spacing={2}>
                    <Paper sx={{ width: "100%", bgcolor: "common.white" }} elevation={0}>
                        <QuotationInfoCardsWrapper title={<Typography variant="h6">{t('general.dynamicSettings')}</Typography>} >
                            <Grid container sx={{ py: 1 }}>
                                <Grid sx={{ borderRight: { xs: "none", md: `1px dashed ${grey[200]}` } }}
                                    size={{ xs: 12, md: 4 }} >
                                    <Grid container spacing={0.5} sx={{ px: 2 }}>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="subtitle2" >
                                                {dynamicSettings[0]?.title}
                                            </Typography>
                                        </Grid>
                                        {dynamicSettings[0]?.details?.map((detail, idx) => (
                                            <Grid size={{ xs: 12 }} key={idx}>
                                                <RowDetails title={detail.title} value={detail.value} disabled={dynamicSettings[0].isDisabled} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>

                                <Grid sx={{ borderRight: { xs: "none", md: `1px dashed ${grey[200]}` } }}
                                    size={{ xs: 12, md: 4 }}
                                >
                                    <Grid container spacing={0.5} sx={{ px: 2 }}>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="subtitle2" sx={{ mt: { xs: 3, md: 0 }, mb: { xs: 1.5, md: 0 } }}>
                                                {dynamicSettings[1]?.title}
                                            </Typography>
                                        </Grid>
                                        {dynamicSettings[1]?.details?.map((detail, idx) => (
                                            <Grid size={{ xs: 12 }} key={idx}>
                                                <RowDetails title={detail.title} value={detail.value} disabled={dynamicSettings[1].isDisabled} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </QuotationInfoCardsWrapper>
                    </Paper>
                    <Paper sx={{ width: "100%", bgcolor: "common.white" }} elevation={0}>
                        <QuotationInfoCardsWrapper key={buyersRentersRaw?.buyerRenterId} title={<Typography variant="h6">{t('buyersGuide.options.generate.buyerRenterInformation')}</Typography>} >
                            <Grid container sx={{ py: 1 }}>
                                <Grid sx={{ borderRight: { xs: "none", md: `1px dashed ${grey[200]}` } }}
                                    size={{ xs: 12, md: 4 }} >
                                    <Grid container spacing={0.5} sx={{ px: 2 }}>
                                        <Grid size={{ xs: 12 }}>
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Typography variant="subtitle2">
                                                    {buyersRenters[0]?.title}
                                                </Typography>
                                                <IconButton size="small" onClick={(() => { setOpenEditRenterInfoDialog(true) })}  >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                        {buyersRenters[0]?.details?.map((detail, idx) => (
                                            <Grid size={{ xs: 12 }} key={idx}>
                                                <RowDetails title={detail.title} value={detail.value} disabled={buyersRenters[0].isDisabled} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>

                                <Grid sx={{ borderRight: { xs: "none", md: `1px dashed ${grey[200]}` } }}
                                    size={{ xs: 12, md: 4 }}
                                >
                                    <Grid container spacing={0.5} sx={{ px: 2 }}>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="subtitle2" sx={{ mt: { xs: 3, md: 0 }, mb: { xs: 1.5, md: 0 } }}>
                                                {buyersRenters[1]?.title}
                                            </Typography>
                                        </Grid>
                                        {buyersRenters[1]?.details?.map((detail, idx) => (
                                            <Grid size={{ xs: 12 }} key={idx}>
                                                <RowDetails title={detail.title} value={detail.value} disabled={buyersRenters[1].isDisabled} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </QuotationInfoCardsWrapper>
                    </Paper>
                </Stack>
            </Box>
            <ConfirmBox
                      open={isOpenGenerateConfirmModal}
                      title={t("general.warning")}
                      description={t("buyersGuide.quotation.generate.confirmation")}
                      onOk={handleConfirmForGenerateQuotation}
                      onCancel={()=>setIsOpenGenerateConfirmModal(false)}
                      okText={t('general.confirm')}
                      cancelText={t('general.cancel')}
             />
            {openEditRenterInfoDialog && <EditBuyerRenterInformationDialog
                open={openEditRenterInfoDialog}
                data={buyersRentersRaw}
                onSave={fetchBuyerInfo}
                onClose={() => setOpenEditRenterInfoDialog(false)}
            /> }
        </Stack>
        }

</>
    )
}

export default memo(GenerateQuotation)
